import { useAuth } from "@/features/auth/composables/useAuth";
import { userDataCache } from "@/shared/services/user-data-cache";
import { templateCache, TEMPLATE_SLOTS } from "@/features/signature/services/template-cache";
import { clearGraphCache } from "@/features/profile/services/graph-cache";
import { profilePermissions } from "@/shared/services/profile-permissions";
import { setupState } from "@/shared/services/setup-state";
import { removeFromStorage } from "@/shared/services/storage";

/**
 * Composable que encapsula a lógica de logout com limpeza completa de todo
 * o cache local (dados do usuário, templates, perfil/foto do Graph e
 * preferências de assinatura), única fonte usada por Profile e Signatures.
 *
 * @returns Função `doLogout` que limpa todo o cache local e executa o logout MSAL.
 *
 * @example
 * ```typescript
 * const { doLogout } = useLogout();
 * await doLogout();
 * ```
 */
export function useLogout() {
  const { logout } = useAuth();

  /**
   * Limpa 100% do cache local (roaming settings e localStorage) e realiza
   * o logout MSAL, redirecionando para a página de login.
   *
   * @returns Promise que resolve quando o logout é concluído.
   *
   * @example
   * ```typescript
   * const { doLogout } = useLogout();
   * await doLogout();
   * ```
   */
  async function doLogout(): Promise<void> {
    userDataCache.clear();
    templateCache.clear();
    clearGraphCache();
    profilePermissions.clear();
    setupState.clear();

    for (const slot of TEMPLATE_SLOTS) removeFromStorage(slot, slot);
    removeFromStorage("override_olk_signature");

    await logout();
  }

  return { doLogout };
}
